import { useTranslation } from 'react-i18next';
import { Wand2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { formatCents } from '@/domain/money';
import { formatDate } from '@/lib/dateUtils';
import type { CollectionIssue } from '@/domain/collectionEngine';
import type { AllocationDraft, CollectableInvoice } from '@/domain/types';

/**
 * Splitting the collected money across the customer's pending invoices.
 *
 * Every pending invoice is listed with its outstanding balance, and the agent
 * types against the ones being settled. "Assign oldest first" fills them
 * automatically, which is what happens in practice — the arithmetic is the
 * part that goes wrong on paper.
 */
export function AllocationRows({
  invoices,
  allocations,
  onChange,
  onAutoAllocate,
  issues,
  collectedCents,
}: {
  invoices: CollectableInvoice[];
  allocations: AllocationDraft[];
  onChange: (allocations: AllocationDraft[]) => void;
  onAutoAllocate: () => void;
  issues: CollectionIssue[];
  collectedCents: number;
}): JSX.Element {
  const { t } = useTranslation();

  const amountFor = (invoiceId: number) =>
    allocations.find((allocation) => allocation.invoiceId === invoiceId)?.amountCents ?? 0;

  const setAmount = (invoiceId: number, amountCents: number) => {
    const others = allocations.filter((allocation) => allocation.invoiceId !== invoiceId);
    onChange(amountCents > 0 ? [...others, { invoiceId, amountCents }] : others);
  };

  const issueFor = (invoiceId: number) => issues.find((issue) => issue.invoiceId === invoiceId);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600">
          {t('collection.pendingInvoices')}{' '}
          <span className="text-slate-400">({invoices.length})</span>
        </p>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onAutoAllocate}
            disabled={collectedCents <= 0}
            leftIcon={<Wand2 className="h-4 w-4" />}
          >
            {t('collection.autoAllocate')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange([])}
            disabled={allocations.length === 0}
            leftIcon={<X className="h-4 w-4" />}
          >
            {t('collection.clearAllocations')}
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {invoices.map((invoice) => {
          const amount = amountFor(invoice.id);
          const issue = issueFor(invoice.id);
          const isFullySettled = amount >= invoice.outstandingCents && amount > 0;

          return (
            <div
              key={invoice.id}
              className={`rounded-lg border p-3 transition-colors ${
                amount > 0 ? 'border-brand-300 bg-brand-50/40' : 'border-slate-200 bg-white'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900">{invoice.invoiceNo}</span>
                    {invoice.isOverdue && <Badge tone="danger">{t('invoice.overdue')}</Badge>}
                    {isFullySettled && <Badge tone="success">{t('invoice.status.PAID')}</Badge>}
                  </div>

                  <p className="mt-0.5 text-xs text-slate-500">
                    {t('invoice.dueDate')}: {formatDate(invoice.dueDate)}
                    {invoice.description ? ` · ${invoice.description}` : ''}
                  </p>

                  <p className="tabular mt-1 text-xs text-slate-600">
                    {t('invoice.amount')}: {formatCents(invoice.amountCents)} ·{' '}
                    <span className="font-semibold text-slate-900">
                      {t('invoice.outstanding')}: {formatCents(invoice.outstandingCents)}
                    </span>
                  </p>
                </div>

                <div className="flex items-end gap-2">
                  <div className="w-40">
                    <MoneyInput
                      value={amount}
                      onChange={(cents) => setAmount(invoice.id, cents)}
                      error={issue ? ' ' : undefined}
                    />
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAmount(invoice.id, invoice.outstandingCents)}
                    title={t('collection.payFull')}
                  >
                    {t('collection.payFull')}
                  </Button>
                </div>
              </div>

              {issue && (
                <p className="mt-2 text-xs text-red-600" role="alert">
                  {t(`collection.issues.${issue.code}` as never, issue.values)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
